import * as stylex from '@stylexjs/stylex'
import { type ElementType } from 'react'
import { type ReactNode } from 'react'
import { Box, type BoxProps } from '../Box/Box'
import { Info } from '../Icons'
import { colors } from '../tokens/colors.stylex'
import { typography } from '../tokens/typography.stylex'
import { type XStyles, useXStyles } from '../useXStyles/useXStyles'

const typeStyles = stylex.create({
  info: {
    backgroundColor: colors.blue100,
    color: colors.grey800,
  },
  warning: {
    backgroundColor: colors.yellow100,
    color: colors.grey800,
  },
  success: {
    backgroundColor: colors.green400,
    color: colors.white,
  },
  error: {
    backgroundColor: colors.red100,
    color: colors.red600,
  },
  grey: {
    backgroundColor: colors.grey100,
    color: colors.grey800,
  },
})

const _baseStyles = stylex.create({
  root: {
    padding: '12px',
    boxSizing: 'border-box',
    display: 'flex',
    fontFamily: typography.font,
    fontSize: typography.sizeCaption,
    fontWeight: typography.weightRegular,
    lineHeight: typography.lineHeightCaption,
  },
  icon: {
    boxSizing: 'border-box',
    display: 'flex',
    marginRight: 8,
  },
  message: {
    boxSizing: 'border-box',
    display: 'flex',
  },
  action: {
    display: 'flex',
    marginLeft: 'auto',
  },
})

export type AlertType = 'info' | 'warning' | 'success' | 'error' | 'grey'

export type AlertProps<RootComponent extends ElementType = 'div', BoxElement extends HTMLElement = HTMLDivElement> =
  BoxProps<RootComponent, BoxElement> & {
    /**
     * 提示類型
     * @default 'info'
     */
    type?: AlertType
    /**
     * 自訂圖示，預設為 Info 圖示
     * @default <Info fontSize="16px" />
     */
    icon?: ReactNode
    /**
     * 右側操作區域內容
     */
    action?: ReactNode
    /**
     * 自訂內部子元素樣式覆寫
     */
    xStyles?: Partial<XStyles<keyof typeof _baseStyles>>
  }

/**
 * Alert 用於向使用者顯示重要的訊息提示。
 * 支援 info / warning / error / success / grey 五種類型。
 */
export const Alert = <RootComponent extends ElementType = 'div', BoxElement extends HTMLElement = HTMLDivElement>(
  props: AlertProps<RootComponent, BoxElement>,
) => {
  const {
    component = 'div',
    type = 'info',
    icon = <Info fontSize="16px" />,
    children,
    action,
    rootStyle,
    xStyles,
    ...rest
  } = props
  const baseStyles = useXStyles(_baseStyles, xStyles)

  return (
    <Box
      component={component}
      rootStyle={[baseStyles.root, typeStyles[type], rootStyle]}
      {...rest}
    >
      {icon && <div {...stylex.props(baseStyles.icon)}>{icon}</div>}
      {children && <div {...stylex.props(baseStyles.message)}>{children}</div>}
      {action && <div {...stylex.props(baseStyles.action)}>{action}</div>}
    </Box>
  )
}
